import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Button from '../../common/Button';
import { Input } from '../../common/FormField';
import { Modal } from '../../common/Modal';
import { jobPhotoAPI } from '../../../services/api';
import { fetchPhotoBlob } from '../../../lib/fetchPhotoBlob';
import { photoExtension } from '../../../lib/photoFile';

const CONFIRM_MS = 4000;

export function formatAdded(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const date = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `Added ${date}, ${time}`;
}

// Hands a blob to the user as a file (HEIC that the browser cannot show, etc.).
export function openBlob(blob, photo) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const ext = photoExtension(photo.content_type);
  a.download = ext ? `photo-${photo.id}.${ext}` : `photo-${photo.id}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function errorText(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  if (!err?.response) return 'Check your connection and try again';
  return fallback;
}

function ViewerBody({ jobId, photo, media, onSaved, onDeleted, onEditingChange }) {
  const [ownMedia, setOwnMedia] = useState(null); // {blob, url} fetched by the viewer itself
  const [loadFailed, setLoadFailed] = useState(false);
  const [decodeFailed, setDecodeFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const mounted = useRef(true);
  const timer = useRef(null);

  useEffect(() => {
    onEditingChange(editing);
    return () => onEditingChange(false);
  }, [editing, onEditingChange]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      clearTimeout(timer.current);
    };
  }, []);

  // Reuse the grid tile's image when it has one; otherwise fetch our own.
  const shared = media && media.url ? media : null;
  useEffect(() => {
    if (shared) return undefined;
    let cancelled = false;
    let url = null;
    const token = { cancelled: false };
    setLoadFailed(false);
    fetchPhotoBlob(jobId, photo.id, token)
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setOwnMedia({ blob, url });
      })
      .catch(() => { if (!cancelled) setLoadFailed(true); });
    return () => {
      cancelled = true;
      token.cancelled = true;
      if (token.wake) token.wake();
      if (url) URL.revokeObjectURL(url);
      setOwnMedia(null);
    };
  }, [jobId, photo.id, shared, attempt]);

  const current = shared || ownMedia;

  const startEdit = () => {
    setDraft(photo.note || '');
    setEditError('');
    setEditing(true);
  };

  const saveNote = async () => {
    if (saving) return;
    setSaving(true);
    setEditError('');
    try {
      const updated = await jobPhotoAPI.updateNote(jobId, photo.id, draft.trim());
      onSaved(updated);
      if (mounted.current) setEditing(false);
    } catch (err) {
      if (mounted.current) setEditError(errorText(err, "Couldn't save the note"));
    } finally {
      if (mounted.current) setSaving(false);
    }
  };

  const onDeleteClick = async () => {
    if (deleting) return;
    if (!confirming) {
      setConfirming(true);
      setDeleteError('');
      timer.current = setTimeout(() => { if (mounted.current) setConfirming(false); }, CONFIRM_MS);
      return;
    }
    clearTimeout(timer.current);
    setDeleting(true);
    setDeleteError('');
    try {
      await jobPhotoAPI.remove(jobId, photo.id);
      onDeleted(photo.id);
    } catch (err) {
      if (!mounted.current) return;
      setConfirming(false);
      setDeleteError(errorText(err, "Couldn't delete the photo"));
    } finally {
      if (mounted.current) setDeleting(false);
    }
  };

  const added = formatAdded(photo.created_at);

  return (
    <div className="space-y-3">
      <div className="bg-surface-sunken rounded-control flex items-center justify-center min-h-[12rem]">
        {current && !decodeFailed ? (
          <img
            src={current.url}
            alt={photo.note || 'Job photo'}
            className="max-w-full max-h-[70vh] object-contain"
            onError={() => setDecodeFailed(true)}
          />
        ) : current && decodeFailed ? (
          <div className="p-4 text-center space-y-2">
            <p className="text-body text-ink-muted">Preview not available</p>
            <Button variant="secondary" size="sm" onClick={() => openBlob(current.blob, photo)}>Open</Button>
          </div>
        ) : loadFailed ? (
          <div className="p-4 text-center space-y-2">
            <p className="text-body text-ink-muted">Couldn't load photo</p>
            <Button variant="secondary" size="sm" onClick={() => setAttempt((a) => a + 1)}>Retry</Button>
          </div>
        ) : (
          <p className="text-small text-ink-muted p-4">Loading…</p>
        )}
      </div>

      {editing ? (
        <div className="space-y-2">
          <Input
            aria-label="Photo note"
            value={draft}
            maxLength={500}
            disabled={saving}
            autoFocus
            onChange={(e) => setDraft(e.target.value)}
          />
          {editError && <p role="alert" className="text-body text-danger">{editError}</p>}
          <div className="flex gap-2">
            <Button className="flex-1" loading={saving} onClick={saveNote}>Save</Button>
            <Button className="flex-1" variant="secondary" disabled={saving} onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {photo.note && <p className="text-body-strong text-ink break-words">{photo.note}</p>}
          <p className="text-small text-ink-muted">
            {added}
            {photo.taken_by_name ? ` · ${photo.taken_by_name}` : ''}
          </p>
          <div className="flex gap-2">
            <Button className="flex-1" variant="secondary" onClick={startEdit}>Edit note</Button>
            <Button className="flex-1" variant="danger" loading={deleting} onClick={onDeleteClick}>
              {confirming ? 'Tap again to delete' : 'Delete'}
            </Button>
          </div>
          {deleteError && <p role="alert" className="text-body text-danger">{deleteError}</p>}
        </div>
      )}
    </div>
  );
}

export default function PhotoViewer({ jobId, photos, currentId, media, onChange, onClose, onSaved, onDeleted }) {
  const index = photos.findIndex((p) => p.id === currentId);
  const photo = index >= 0 ? photos[index] : null;
  const prev = index > 0 ? photos[index - 1] : null;
  const next = index >= 0 && index < photos.length - 1 ? photos[index + 1] : null;

  const [editing, setEditing] = useState(false);
  const prevRef = useRef(prev);
  const nextRef = useRef(next);
  prevRef.current = prev;
  nextRef.current = next;
  const editingRef = useRef(false);
  editingRef.current = editing;

  // Escape is handled by Modal; arrows are ours, and must not hijack the note field.
  useEffect(() => {
    if (!photo) return undefined;
    const onKey = (e) => {
      if (editingRef.current) return;
      const tag = e.target && e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 'ArrowLeft' && prevRef.current) onChange(prevRef.current.id);
      if (e.key === 'ArrowRight' && nextRef.current) onChange(nextRef.current.id);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [photo, onChange]);

  const handleDeleted = useCallback((id) => {
    onDeleted(id, (next || prev)?.id ?? null);
  }, [onDeleted, next, prev]);

  if (!photo) return null;

  return (
    <Modal isOpen onClose={onClose} title={`Photo ${index + 1} of ${photos.length}`} size="xl">
      <ViewerBody
        key={photo.id}
        jobId={jobId}
        photo={photo}
        media={media[photo.id]}
        onSaved={onSaved}
        onDeleted={handleDeleted}
        onEditingChange={setEditing}
      />
      <div className="flex gap-2 mt-3">
        <Button className="flex-1" variant="secondary" icon={ChevronLeft} disabled={!prev || editing} onClick={() => prev && onChange(prev.id)}>
          Previous
        </Button>
        <Button className="flex-1" variant="secondary" icon={ChevronRight} iconPosition="right" disabled={!next || editing} onClick={() => next && onChange(next.id)}>
          Next
        </Button>
      </div>
    </Modal>
  );
}
